/**
 * On-device error ring buffer. Captures client-side crashes that can't be
 * reported live (e.g. iOS App hard-wedge while offline — no PostHog flush, no
 * server log, no console). Surfaced in DebugDiagnostics so a tester can read
 * the last few errors after force-closing and reopening the app.
 *
 * Shape mirrored by the inline pre-hydration script in layout.tsx, which
 * writes the same key for window-level errors that fire before any React
 * boundary mounts. Keep the key + entry shape in sync with that snippet.
 */

import { apiFetch } from '@/lib/api'

export const ERROR_BUFFER_KEY = 'zs_last_errors'
const MAX_ENTRIES = 10
const MAX_MESSAGE_CHARS = 1000
const MAX_STACK_CHARS = 3000

// Mirrored by the inline pre-hydration script in src/app/layout.tsx as raw
// strings (no compile-time link). Renaming a member here means hand-updating
// the inline pushErr() call sites too.
export type ErrorBoundary =
  | 'segment'        // src/app/error.tsx
  | 'global'         // src/app/global-error.tsx
  | 'window-error'   // window 'error' event (script-load, sync throw outside React)
  | 'window-rejection' // window 'unhandledrejection'
  | 'chunk-recovery' // pre-hydration script triggered cache nuke

export interface ErrorEntry {
  ts: number
  boundary: ErrorBoundary
  message: string
  digest?: string
  stack?: string
  url: string
  ua?: string
}

export function appendError(entry: Omit<ErrorEntry, 'ts' | 'url' | 'ua'>): void {
  if (typeof window === 'undefined') return
  try {
    const full: ErrorEntry = {
      ts: Date.now(),
      url: window.location.href,
      ua: window.navigator?.userAgent,
      ...entry,
      message: entry.message.slice(0, MAX_MESSAGE_CHARS),
      stack: entry.stack ? entry.stack.slice(0, MAX_STACK_CHARS) : undefined,
    }
    const raw = window.localStorage.getItem(ERROR_BUFFER_KEY)
    const list: ErrorEntry[] = raw ? safeParse(raw) : []
    // Throttle identical messages within 1s — prevents an error loop (e.g.
    // a setState in componentDidUpdate) from amplifying via the listener
    // itself.
    const last = list[list.length - 1]
    if (last && last.message === full.message && full.ts - last.ts < 1000) return
    list.push(full)
    while (list.length > MAX_ENTRIES) list.shift()
    try {
      window.localStorage.setItem(ERROR_BUFFER_KEY, JSON.stringify(list))
    } catch {
      // Quota exceeded — drop everything but the most recent entry, retry.
      try {
        window.localStorage.setItem(ERROR_BUFFER_KEY, JSON.stringify([full]))
      } catch { /* give up */ }
    }
  } catch {
    /* never let error capture throw — the page is already broken */
  }
}

export function readErrors(): ErrorEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(ERROR_BUFFER_KEY)
    return raw ? safeParse(raw) : []
  } catch {
    return []
  }
}

export function clearErrors(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(ERROR_BUFFER_KEY) } catch { /* noop */ }
}

// History API call buffer — written by the inline pre-hydration script in
// layout.tsx that wraps history.replaceState/pushState. Used to diagnose the
// Safari SecurityError after 100 replaceState in 10s by surfacing the offending
// call sites in DebugDiagnostics. Shape mirrored in the inline script.
export const HISTORY_BUFFER_KEY = 'zs_history_calls'

export interface HistoryCallEntry {
  ts: number
  // `replace` / `push` come from the inline-script wrapper around the
  // history API. `router-*` come from appendRouterCall in this file —
  // captured at the app-code call site (useful stack) before Next's
  // reducer runs. Same buffer so they interleave in time order.
  // `replace-skip` = a redundant replaceState that the inline-script wrapper
  // suppressed (same URL + same tree content hash as the prior call). Logged
  // for visibility into how many calls the dedupe blocked.
  kind: 'replace' | 'replace-skip' | 'push' | 'router-push' | 'router-replace' | 'router-refresh' | 'router-prefetch'
  url: string
  rate: number
  stack: string
  sess?: string
  // Fingerprint of the `state` arg passed to history.replaceState/pushState.
  // Format: `<stateRefId>/<segmentLabel>`. Lets us tell apart "same state
  // dispatched twice" (Next bug — equal state, different reference) from
  // "real state churn" (different content). Empty for router-* entries.
  fp?: string
}

export function readHistoryCalls(): HistoryCallEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(HISTORY_BUFFER_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function clearHistoryCalls(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(HISTORY_BUFFER_KEY) } catch { /* noop */ }
}

/**
 * Capture a `router.push|replace|refresh|prefetch` call into the same
 * `zs_history_calls` buffer with a `router-*` kind. Stack from `new Error()`
 * tells us the app-code site that called the router method — unlike the
 * replaceState wrapper, this captures *outside* React's commit phase so
 * the stack is informative.
 *
 * Replaces the inline-script wrapper's per-sess cap check by sharing the
 * same buffer key — the cap is enforced when the inline script writes
 * replaceState entries, so router entries can interleave but the total
 * buffer stays bounded by HMAX in layout.tsx.
 */
export function appendRouterCall(method: 'push' | 'replace' | 'refresh' | 'prefetch', url: string): void {
  if (typeof window === 'undefined') return
  try {
    const sess = (window as unknown as { __zsSessId?: string }).__zsSessId
    const stack = (new Error().stack || '').slice(0, 2000)
    const entry: HistoryCallEntry = {
      ts: Date.now(),
      kind: ('router-' + method) as HistoryCallEntry['kind'],
      url: String(url || '').slice(0, 200),
      rate: 0,
      sess,
      stack,
    }
    const raw = window.localStorage.getItem(HISTORY_BUFFER_KEY)
    let list: HistoryCallEntry[] = []
    if (raw) {
      try { const p = JSON.parse(raw); if (Array.isArray(p)) list = p } catch { /* noop */ }
    }
    list.push(entry)
    while (list.length > 300) list.shift()
    try { window.localStorage.setItem(HISTORY_BUFFER_KEY, JSON.stringify(list)) } catch { /* quota — drop */ }
  } catch { /* never let capture throw */ }
}

// Watermark keys: highest `ts` already shipped to /api/debug/log. Used to
// suppress re-uploading entries the server already has on each flush attempt.
const ERROR_WATERMARK_KEY = 'zs_last_errors_sent_ts'
const HISTORY_WATERMARK_KEY = 'zs_history_calls_sent_ts'

function readNumber(key: string): number {
  if (typeof window === 'undefined') return 0
  try {
    const raw = window.localStorage.getItem(key)
    const n = raw ? Number(raw) : 0
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

function writeNumber(key: string, value: number): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(key, String(value)) } catch { /* noop */ }
}

let flushInFlight = false

/**
 * POST any unshipped client-error / history-call entries to /api/debug/log.
 * Idempotent (watermarks track what's been shipped). Silently no-ops offline
 * — caller should retry on next online transition. Returns count shipped.
 */
export async function flushDebugBuffersToServer(): Promise<number> {
  if (typeof window === 'undefined') return 0
  if (flushInFlight) return 0
  flushInFlight = true
  try {
    const errorsAll = readErrors()
    const callsAll = readHistoryCalls()
    const errorMark = readNumber(ERROR_WATERMARK_KEY)
    const callMark = readNumber(HISTORY_WATERMARK_KEY)
    const errors = errorsAll.filter((e) => e.ts > errorMark)
    const historyCalls = callsAll.filter((c) => c.ts > callMark)
    if (errors.length === 0 && historyCalls.length === 0) return 0

    const res = await apiFetch('/api/debug/log', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors, historyCalls }),
    })
    if (!res.ok) return 0

    const newErrorMark = errors.reduce((acc, e) => Math.max(acc, e.ts), errorMark)
    const newCallMark = historyCalls.reduce((acc, c) => Math.max(acc, c.ts), callMark)
    if (newErrorMark > errorMark) writeNumber(ERROR_WATERMARK_KEY, newErrorMark)
    if (newCallMark > callMark) writeNumber(HISTORY_WATERMARK_KEY, newCallMark)
    return errors.length + historyCalls.length
  } catch {
    return 0
  } finally {
    flushInFlight = false
  }
}

function safeParse(raw: string): ErrorEntry[] {
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}
